import React from 'react'
import type { GetStaticProps } from 'next'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import CityLinks from '@src/components/CityLinks'
import { getCityLinks, type CityLinksData } from '@src/content/cities'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import hazardousWaste from '../../json/hazardous-waste-disposal-data.json'

const hazardousServiceData = {
  name: 'Hazardous Waste Disposal',
  description:
    'DOT- and RCRA-compliant hazardous waste disposal for businesses. Dimension classifies, collects, transports and documents every hazardous stream — ignitable, corrosive, reactive and toxic — from 1-gallon cans to 550-gallon totes, with full manifesting.',
  serviceType: 'Hazardous Waste Disposal',
  url: 'https://getdimension.com/hazardous-waste-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Ignitable and Flammable Waste Disposal' },
    { name: 'Corrosive Waste Disposal' },
    { name: 'Reactive Waste Disposal' },
    { name: 'Toxic and Heavy Metal Waste Disposal' },
    { name: 'Hazardous Waste Manifesting and Documentation' },
  ],
}

const hazardousFAQData = {
  questions: hazardousWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

type Props = { cityLinks: CityLinksData | null }

/**
 * Built at build time so the city registry stays out of the client bundle —
 * see getCityLinks. Without this the page ships every city's full content.
 */
export const getStaticProps: GetStaticProps<Props> = async () => ({
  props: { cityLinks: getCityLinks('hazardous-waste-disposal') },
})

function HazardousWasteDisposal({ cityLinks }: Props) {
  return (
    <>
      <Head>
        <title>Hazardous Waste Disposal Services for Businesses | Dimension</title>
        <meta
          name="description"
          content="DOT & RCRA compliant hazardous waste disposal for businesses. Ignitable, corrosive, reactive and toxic waste collected nationwide, fully manifested. Instant quote, under half competitor rates."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/hazardous-waste-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={hazardousServiceData} />
        <SchemaMarkup type="FAQPage" data={hazardousFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="hazardous-drums"
          heading={hazardousWaste.pitch.heading}
          material={hazardousWaste.pitch.material}
          pitchText={hazardousWaste.pitch.pitchText}
          ctaLink={hazardousWaste.pitch.ctaLink}
          ctaText={hazardousWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...hazardousWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...hazardousWaste.benefits} />
        <CaseStudy {...hazardousWaste.caseStudy} />
        <Approach {...hazardousWaste.approach} />
        <FeatureList {...hazardousWaste.storage} />
        <FAQ {...hazardousWaste.FAQContent} />
        <CityLinks data={cityLinks} />
        <RelatedServices
          services={[
            {
              label: 'Flammable Waste Disposal',
              href: '/flammable-waste-disposal',
              description:
                'The ignitable (D001) stream — solvents, used fuel, aerosols, and other DOT Class 3 waste.',
            },
            {
              label: 'Corrosive Waste Disposal',
              href: '/corrosive-waste-disposal',
              description:
                'The corrosive (D002) stream — waste acids, caustics, plating baths, and battery acid.',
            },
            {
              label: 'Toxic Waste Disposal',
              href: '/toxic-waste-disposal',
              description:
                'The toxic stream — heavy metals, pesticides, and contaminated liquids that leach regulated contaminants.',
            },
            {
              label: 'Other Hazardous Waste',
              href: '/other-hazardous-waste',
              description:
                'The catch-all for mixed, unlabeled, and hard-to-classify chemical loads that span several streams.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default HazardousWasteDisposal
